import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { useEditTradeRequestPartnerIdentifier } from 'services/repositories/relationships/relationships';
import { addNotification } from 'stores/notifications/notifications';
import { processSubmissionErrors } from 'support/helpers/errors/errors';
import { IdentifierDomainOptions, RelationshipV2 } from 'support/types';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';
import { InlineInput } from 'components/Form/InlineInput/InlineInput';

export const InternalIdentifierEditor = ({ relationship }: { relationship: RelationshipV2 }) => {
  const { t } = useTranslation();
  const { mutateAsync } = useEditTradeRequestPartnerIdentifier();

  const {
    register,
    formState: { errors },
    setError,
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      partyIdentifier: relationship.trade_request?.partyIdentifier || '',
      partyIdentifierDomain: IdentifierDomainOptions.RECEIVER_INTERNAL,
    },
    resolver: yupResolver(
      Yup.object().shape({
        partyIdentifier: Yup.string().required(t('connections:relationshipDetails.header.internalIdentifier.required')),
        partyIdentifierDomain: Yup.string().required(),
      }),
    ),
    mode: 'onChange',
  });

  const onSubmit = async (data: { partyIdentifier: string }) => {
    try {
      await mutateAsync({
        relationshipId: relationship.id,
        partyIdentifier: data.partyIdentifier,
        partyIdentifierDomain: IdentifierDomainOptions.RECEIVER_INTERNAL,
      });
      addNotification(t('connections:relationshipDetails.header.internalIdentifier.success'));
    } catch (error) {
      processSubmissionErrors({
        error: error as Error,
        t,
        setNonInputErrors: undefined,
        setInputError: setError,
        defaultData: {
          partyIdentifier: '',
          partyIdentifierDomain: IdentifierDomainOptions.RECEIVER_INTERNAL,
        },
      });
      throw error;
    }
  };

  if (relationship.trade_request?.partyIdentifierDomain !== IdentifierDomainOptions.RECEIVER_INTERNAL) {
    return null;
  }

  return (
    <div className="flex min-h-10 flex-col justify-center py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-1 items-start gap-1">
          <span className="text-xs text-gray-500">
            {t('connections:relationshipDetails.header.internalIdentifier.title')}
          </span>
          <Tooltip>
            <TooltipTrigger>
              <InformationCircleIcon className="size-4 min-h-4 min-w-4 text-gray-400" />
            </TooltipTrigger>
            <TooltipContent>{t('connections:relationshipDetails.header.internalIdentifier.tooltip')}</TooltipContent>
          </Tooltip>
        </div>
        <InlineInput
          value={watch('partyIdentifier')}
          error={errors.partyIdentifier}
          {...register('partyIdentifier')}
          onSave={async () => await handleSubmit(onSubmit)()}
          autoComplete="off"
          data-1p-ignore // stops 1password from showing in the input
        />
      </div>
    </div>
  );
};
